import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaChartLine, FaRegClone, FaArrowLeft } from 'react-icons/fa';
import RightWrapper from '../../../Components/CommonComponents/ComponentWrapper';
import CustomBreadcrumbs from '../../../Components/CommonComponents/CustomBreadcrumbs';
import CustomSpinner from '../../../Components/CommonComponents/CustomSpinner';
import ConfirmDialog from '../../../Components/CommonComponents/ConfirmDialog';
import VitalTemplateBuilder, { emptyField } from './VitalTemplateBuilder';
import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from '../../../Services/patientProfile';

const blankDraft = () => ({ name: '', description: '', frequency: 'Daily', fields: [] });

const toDraft = (template) => ({
  name: template.name || '',
  description: template.description || '',
  frequency: template.frequency || 'Daily',
  // `order` is deliberately dropped: the saved value would otherwise win over
  // the array position on save, silently undoing any reordering in the editor.
  fields: (template.fields || []).map(({ order, ...f }) =>
    emptyField({
      ...f,
      min: f.min ?? '',
      max: f.max ?? '',
      normalLow: f.normalLow ?? '',
      normalHigh: f.normalHigh ?? '',
    })
  ),
});

const TemplateCard = ({ template, onEdit, onDuplicate, onDelete }) => {
  const charted = (template.fields || []).filter((f) => f.chart && f.type === 'number').length;

  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-primary-300 hover:shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-slate-800">{template.name}</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {template.frequency} · {(template.fields || []).length} field
            {(template.fields || []).length === 1 ? '' : 's'}
            {charted > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 text-primary-600">
                · <FaChartLine className="inline" /> {charted} graphed
              </span>
            )}
          </p>
        </div>
        {!template.active && (
          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
            Retired
          </span>
        )}
      </div>

      {template.description && (
        <p className="mt-3 line-clamp-2 text-sm text-slate-600">{template.description}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {(template.fields || []).slice(0, 6).map((f) => (
          <span key={f.key} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
            {f.label}
            {f.unit ? ` (${f.unit})` : ''}
          </span>
        ))}
        {(template.fields || []).length > 6 && (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
            +{template.fields.length - 6} more
          </span>
        )}
      </div>

      <div className="mt-4 flex gap-2 border-t border-slate-100 pt-3">
        <button
          onClick={() => onEdit(template)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          <FaEdit /> Edit
        </button>
        <button
          onClick={() => onDuplicate(template)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          <FaRegClone /> Duplicate
        </button>
        <button
          onClick={() => onDelete(template)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50"
        >
          <FaTrash /> Delete
        </button>
      </div>
    </div>
  );
};

export default function VitalTemplates() {
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null); // null = list view, {} = new, {_id} = edit
  const [draft, setDraft] = useState(blankDraft());
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setTemplates(await fetchTemplates({ active: 'all' }));
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not load vital templates');
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setDraft(blankDraft());
    setEditing({});
  };

  const startEdit = (template) => {
    setDraft(toDraft(template));
    setEditing(template);
  };

  const startDuplicate = (template) => {
    // Keys are minted fresh on save, so the copy never shares history with the
    // original — clearing them here makes that explicit.
    const copy = toDraft(template);
    setDraft({ ...copy, name: `${copy.name} (copy)`, fields: copy.fields.map((f) => ({ ...f, key: '' })) });
    setEditing({});
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error('Give the template a name');
    if (!draft.fields.length) return toast.error('Add at least one field');
    if (draft.fields.some((f) => !String(f.label || '').trim())) {
      return toast.error('Every field needs a label');
    }

    setSaving(true);
    try {
      if (editing?._id) {
        await updateTemplate(editing._id, draft);
        toast.success('Template updated');
      } else {
        await createTemplate(draft);
        toast.success('Template created');
      }
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not save the template');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await deleteTemplate(pendingDelete._id);
      toast.success(res?.message || 'Template deleted');
      setPendingDelete(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not delete the template');
    }
  };

  if (editing) {
    return (
      <RightWrapper>
        <div className="p-3 md:p-6">
          <button
            onClick={() => setEditing(null)}
            className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            <FaArrowLeft /> Back to templates
          </button>

          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                {editing?._id ? 'Edit vitals template' : 'New vitals template'}
              </h1>
              <p className="text-sm text-slate-500">
                Design the form once, then assign it to any patient from their profile.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setEditing(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="rounded-lg bg-primary-500 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>

          <VitalTemplateBuilder draft={draft} setDraft={setDraft} />
        </div>
      </RightWrapper>
    );
  }

  return (
    <RightWrapper>
      <div className="p-3 md:p-6">
        <CustomBreadcrumbs data={[{ title: 'Dashboard', url: '/dashboard' }, { title: 'Vitals Templates' }]} />

        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Vitals templates</h1>
            <p className="text-sm text-slate-500">
              Custom forms for recording vital signs. Numeric fields become trend graphs on the patient profile.
            </p>
          </div>
          <button
            onClick={startNew}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            <FaPlus /> New template
          </button>
        </div>

        {templates === null ? (
          <CustomSpinner />
        ) : templates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <FaChartLine className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">No vitals templates yet</p>
            <p className="mt-1 text-sm text-slate-500">
              Build your first form — blood pressure, blood sugar, a symptom diary, anything you want tracked.
            </p>
            <button
              onClick={startNew}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
            >
              <FaPlus /> New template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {templates.map((template) => (
              <TemplateCard
                key={template._id}
                template={template}
                onEdit={startEdit}
                onDuplicate={startDuplicate}
                onDelete={setPendingDelete}
              />
            ))}
          </div>
        )}

        <ConfirmDialog
          isOpen={!!pendingDelete}
          title="Delete this template?"
          message={
            pendingDelete
              ? `"${pendingDelete.name}" will be removed. If any readings were recorded against it, it is retired instead so past graphs keep working.`
              : ''
          }
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      </div>
    </RightWrapper>
  );
}
